import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Users, ArrowRight, ChevronDown } from "lucide-react";
import leaveService from "@/services/leaveService";
import EmployeesOnLeaveModal from "./EmployeesOnLeaveModal";
import { formatDuration } from "@/utils/dateUtils";

// Deterministic avatar palette based on name
const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-green-100 text-green-700",
  "bg-orange-100 text-orange-700",
  "bg-pink-100 text-pink-700",
  "bg-teal-100 text-teal-700",
  "bg-indigo-100 text-indigo-700",
  "bg-rose-100 text-rose-700",
];

const getInitials = (leave) => {
  const first = leave.Employee?.User?.first_name || "";
  const last = leave.Employee?.User?.last_name || "";
  if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return "?";
};

const getAvatarColor = (leave) => {
  const name = `${leave.Employee?.User?.first_name || ""}${leave.Employee?.User?.last_name || ""}`;
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 997;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const getDaysUntilReturn = (endDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const returnDate = new Date(endDate);
  returnDate.setHours(0, 0, 0, 0);
  return Math.round((returnDate - today) / (1000 * 60 * 60 * 24));
};

const EmployeesOnLeave = ({ limit = 10, className = "" }) => {
  // `leaves` holds the full list (used by the modal + count);
  // only `limit` are shown inline.
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await leaveService.getCurrentLeaves({ limit: 100 });
      // leaveService.getCurrentLeaves returns the unwrapped body, so the
      // leaves array lives at response.data.leaves.
      setLeaves(response?.data?.leaves || []);
      setError(false);
    } catch (err) {
      console.error("Failed to fetch employees on leave:", err);
      setError(true);
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
    // Refresh every 5 minutes so the widget stays current
    const interval = setInterval(fetchLeaves, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleLeaves = leaves.slice(0, limit);

  return (
    <>
      <div
        className={`bg-white rounded-2xl p-6 shadow-sm ${className}`}
        style={{ border: "0.5px solid #e5e7eb" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
              <CalendarDays className="h-5 w-5 text-[#DF6B38]" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Employees on Leave
              </h3>
              <p className="text-xs text-gray-500">Currently away from work</p>
            </div>
          </div>
          {!loading && !error && leaves.length > 0 && (
            <button
              onClick={() => setShowModal(true)}
              title="View all employees on leave"
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-all duration-150 group ${
                leaves.length > 0
                  ? "bg-orange-50 text-[#DF6B38] hover:bg-orange-100"
                  : "bg-green-50 text-green-600 hover:bg-green-100"
              }`}
            >
              {leaves.length} {leaves.length === 1 ? "person" : "people"}
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-y-0.5" />
            </button>
          )}
          {!loading && !error && leaves.length === 0 && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-green-600">
              0 people
            </span>
          )}
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="animate-pulse flex items-center gap-3">
                <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="text-center py-8">
            <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              Couldn't load employees on leave
            </p>
            <button
              onClick={fetchLeaves}
              className="mt-3 text-sm font-medium text-[#DF6B38] hover:text-[#c85f30]"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && leaves.length === 0 && (
          <div className="text-center py-8">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-green-400 rounded-full blur-md opacity-20"></div>
              <Users className="relative h-10 w-10 text-green-500 mx-auto mb-3" />
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Everyone is in the office today 🎉
            </p>
            <p className="text-xs text-gray-500">
              No employees currently on leave
            </p>
          </div>
        )}

        {/* Leave list */}
        {!loading && !error && visibleLeaves.length > 0 && (
          <>
            <div className="space-y-2.5">
              {visibleLeaves.map((leave) => {
                const daysLeft = getDaysUntilReturn(leave.end_date);
                return (
                  <div
                    key={leave.id}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-orange-50/50 transition-colors duration-150 border border-transparent hover:border-orange-100"
                  >
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${getAvatarColor(
                        leave,
                      )}`}
                    >
                      {getInitials(leave)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {leave.Employee?.User
                            ? `${leave.Employee.User.first_name} ${leave.Employee.User.last_name}`
                            : "Unknown"}
                        </p>
                        <span className="flex items-center gap-1.5 text-[11px] text-gray-500 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full truncate">
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor:
                                leave.LeaveType?.color || "#DF6B38",
                            }}
                          />
                          {leave.LeaveType?.name || leave.leave_type || "Leave"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {leave.Employee?.Department?.name || "Department"} •{" "}
                        {formatDuration(leave.total_days)}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[11px] font-medium text-gray-700">
                        Returns{" "}
                        {new Date(leave.end_date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </p>
                      <p
                        className={`text-[11px] ${
                          daysLeft <= 1
                            ? "text-green-600 font-medium"
                            : "text-gray-400"
                        }`}
                      >
                        {daysLeft <= 0
                          ? "today"
                          : daysLeft === 1
                            ? "tomorrow"
                            : `in ${daysLeft} days`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer actions */}
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center gap-3">
              <button
                onClick={() => setShowModal(true)}
                className="w-full sm:w-auto flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-150 shadow-sm hover:shadow-md"
              >
                <Users className="h-4 w-4" />
                View all on leave
                {leaves.length > visibleLeaves.length && (
                  <span className="bg-white/25 px-1.5 py-0.5 rounded-md text-[11px] font-bold">
                    {leaves.length}
                  </span>
                )}
              </button>
              <Link
                to="/hr/leaves"
                className="flex items-center justify-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#DF6B38] transition-colors duration-150"
              >
                Manage leave requests
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Full list modal */}
      <EmployeesOnLeaveModal
        open={showModal}
        onClose={() => setShowModal(false)}
        leaves={leaves}
      />
    </>
  );
};

export default EmployeesOnLeave;
